import type {
  Admin,
  AdminSnapshot,
  Campaign,
  Cart,
  CatalogItem,
  CheckoutResult,
  Customer,
  NetworkSnapshot,
  Order,
  PaymentConfirmation,
  PaymentResult,
  PublicSiteBootstrap,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | null;

  constructor(message: string, status: number, code: string, correlationId: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

type Authority = "customer" | "admin" | undefined;
type RequestOptions = Omit<RequestInit, "body"> & { readonly body?: unknown; readonly authority?: Authority };

interface ErrorPayload {
  readonly error?: { readonly message?: string; readonly code?: string; readonly correlationId?: string };
}

export class ApiClient {
  private customerCsrf: string | null = null;
  private adminCsrf: string | null = null;

  private csrfFor(authority: Authority): string | null {
    return authority === "admin" ? this.adminCsrf : authority === "customer" ? this.customerCsrf : null;
  }

  private setCsrf(authority: Authority, value: string): void {
    if (authority === "admin") this.adminCsrf = value;
    if (authority === "customer") this.customerCsrf = value;
  }

  private clearCsrf(authority: Authority): void {
    if (authority === "admin") this.adminCsrf = null;
    if (authority === "customer") this.customerCsrf = null;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? "GET";
    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      headers.set("X-NCP-Request", "1");
      const csrf = this.csrfFor(options.authority);
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }

    const response = await fetch(path, {
      ...options,
      method,
      headers,
      credentials: "same-origin",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json") ? ((await response.json()) as unknown) : null;
    if (!response.ok) {
      const error = payload as ErrorPayload | null;
      throw new ApiError(
        error?.error?.message ?? `La operación no pudo completarse (${response.status}).`,
        response.status,
        error?.error?.code ?? "http_error",
        error?.error?.correlationId ?? response.headers.get("x-correlation-id"),
      );
    }
    return payload as T;
  }

  async getBootstrap(): Promise<PublicSiteBootstrap> {
    return this.request<PublicSiteBootstrap>("/v1/site/bootstrap");
  }

  async getCatalog(): Promise<readonly CatalogItem[]> {
    const result = await this.request<{ readonly items: readonly CatalogItem[] }>("/v1/store/catalog");
    return result.items;
  }

  async getCustomer(): Promise<Customer | null> {
    try {
      const result = await this.request<{ readonly customer: Customer }>("/v1/account/me");
      return result.customer;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  async registerCustomer(email: string, password: string): Promise<void> {
    await this.request("/v1/account/registrations", { method: "POST", body: { email, password }, authority: "customer" });
  }

  async loginCustomer(email: string, password: string): Promise<Customer> {
    const result = await this.request<{ readonly customer: Customer }>("/v1/account/sessions", {
      method: "POST",
      body: { email, password },
      authority: "customer",
    });
    await this.refreshCustomerCsrf();
    return result.customer;
  }

  async refreshCustomerCsrf(): Promise<void> {
    const result = await this.request<{ readonly csrfToken: string }>("/v1/account/csrf");
    this.setCsrf("customer", result.csrfToken);
  }

  async logoutCustomer(): Promise<void> {
    await this.request<void>("/v1/account/sessions/current", { method: "DELETE", authority: "customer" });
    this.clearCsrf("customer");
  }

  async getAdmin(): Promise<Admin | null> {
    try {
      const result = await this.request<{ readonly admin: Admin }>("/v1/admin-auth/me");
      return result.admin;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  async loginAdmin(email: string, password: string): Promise<Admin> {
    const result = await this.request<{ readonly admin: Admin }>("/v1/admin-auth/sessions", {
      method: "POST",
      body: { email, password },
      authority: "admin",
    });
    await this.refreshAdminCsrf();
    return result.admin;
  }

  async refreshAdminCsrf(): Promise<void> {
    const result = await this.request<{ readonly csrfToken: string }>("/v1/admin-auth/csrf");
    this.setCsrf("admin", result.csrfToken);
  }

  async logoutAdmin(): Promise<void> {
    await this.request<void>("/v1/admin-auth/sessions/current", { method: "DELETE", authority: "admin" });
    this.clearCsrf("admin");
  }

  async getCart(cartId: string): Promise<Cart> {
    const result = await this.request<{ readonly cart: Cart }>(`/v1/store/carts/${encodeURIComponent(cartId)}`, { authority: "customer" });
    return result.cart;
  }

  async createCart(): Promise<Cart> {
    const result = await this.request<{ readonly cart: Cart }>("/v1/store/carts", {
      method: "POST",
      body: { currency: "MXN" },
      authority: "customer",
    });
    return result.cart;
  }

  async addToCart(cartId: string, offerRef: string): Promise<Cart> {
    const result = await this.request<{ readonly cart: Cart }>(`/v1/store/carts/${encodeURIComponent(cartId)}/items`, {
      method: "POST",
      body: { offerRef, quantity: 1 },
      authority: "customer",
    });
    return result.cart;
  }

  async checkout(cartId: string, trackingCode: string | null = null): Promise<CheckoutResult> {
    return this.request<CheckoutResult>("/v1/store/checkout", {
      method: "POST",
      body: { cartId, trackingCode },
      authority: "customer",
    });
  }

  async visitClone(code: string): Promise<void> {
    await this.request<{ readonly status: "accepted"; readonly destinationPath: string }>(`/v1/clone/visit/${encodeURIComponent(code)}`, { method: "POST" });
  }

  async createPayment(orderId: string): Promise<PaymentResult> {
    return this.request<PaymentResult>("/v1/store/payments", {
      method: "POST",
      body: { orderId, idempotencyKey: `frontend-demo-${orderId}` },
      authority: "customer",
    });
  }

  async confirmPayment(paymentIntentId: string): Promise<PaymentConfirmation> {
    return this.request<PaymentConfirmation>(`/v1/store/payments/${encodeURIComponent(paymentIntentId)}/confirm`, {
      method: "POST",
      body: { providerEventId: `frontend-demo-confirmed-${paymentIntentId}` },
      authority: "customer",
    });
  }

  async getOrders(): Promise<readonly Order[]> {
    const result = await this.request<{ readonly orders: readonly Order[] }>("/v1/customer/orders", { authority: "customer" });
    return result.orders;
  }

  async getNetwork(): Promise<NetworkSnapshot> {
    return this.request<NetworkSnapshot>("/v1/customer/network", { authority: "customer" });
  }

  async getCampaigns(): Promise<readonly Campaign[]> {
    const result = await this.request<{ readonly campaigns: readonly Campaign[] }>("/v1/customer/marketing/campaigns", { authority: "customer" });
    return result.campaigns;
  }

  async createCampaign(input: { readonly name: string; readonly objective: string }): Promise<Campaign> {
    const result = await this.request<{ readonly campaign: Campaign }>("/v1/customer/marketing/campaigns", {
      method: "POST",
      body: input,
      authority: "customer",
    });
    return result.campaign;
  }

  async createPromotion(input: { readonly campaignId: string; readonly name: string; readonly assetRef: string }): Promise<{ readonly id: string }> {
    const result = await this.request<{ readonly promotion: { readonly id: string } }>("/v1/customer/marketing/promotions", {
      method: "POST",
      body: { ...input, assetType: "offer" },
      authority: "customer",
    });
    return result.promotion;
  }

  async createCreative(input: { readonly promotionId: string; readonly headline: string }): Promise<{ readonly id: string }> {
    const result = await this.request<{ readonly creative: { readonly id: string } }>("/v1/customer/marketing/creatives", {
      method: "POST",
      body: { ...input, name: "Material principal", format: "text", body: "Material DEMO editable para compartir manualmente." },
      authority: "customer",
    });
    return result.creative;
  }

  async activateCampaign(campaignId: string): Promise<void> {
    await this.request(`/v1/customer/marketing/campaigns/${encodeURIComponent(campaignId)}/activate`, { method: "POST", authority: "customer" });
  }

  async activatePromotion(promotionId: string): Promise<void> {
    await this.request("/v1/customer/marketing/promotions/activate", { method: "POST", body: { promotionId }, authority: "customer" });
  }

  async createTrackingLink(input: { readonly campaignId: string; readonly promotionId: string; readonly creativeId: string }): Promise<{ readonly code: string }> {
    const result = await this.request<{ readonly link: { readonly code: string } }>("/v1/customer/marketing/tracking-links", {
      method: "POST",
      body: { ...input, platform: "DIRECT", placement: "demo", destinationUrl: "/store", shareText: "Conoce esta opción DEMO." },
      authority: "customer",
    });
    return result.link;
  }

  async getAdminSnapshot(): Promise<AdminSnapshot> {
    const [marketing, audit] = await Promise.all([
      this.request<{ readonly campaigns: readonly Campaign[]; readonly metrics: readonly { readonly event_type: string; readonly count: number }[] }>("/v1/admin/marketing", { authority: "admin" }),
      this.request<{ readonly events: readonly Readonly<Record<string, unknown>>[] }>("/v1/admin/audit", { authority: "admin" }),
    ]);
    return { campaigns: marketing.campaigns, metrics: marketing.metrics, events: audit.events };
  }
}
